import type { PendingReaction } from "../types/combat.js";
import { EventBus } from "../EventBus.js";
import { cooldownClock } from "./CooldownClock.js";

const REACTION_WINDOW_SEC = 1.5;
const REACTION_TIMER_ID = "combat:reaction:window";

let pending: PendingReaction | null = null;

/**
 * Opens a reaction window for the given combat event.
 * If the player does not respond within REACTION_WINDOW_SEC, the event
 * resolves automatically. The countdown respects world-freeze — it pauses
 * whenever cooldownClock.tick() is not called (dialogue open, menu, etc.).
 */
export function openReactionWindow(reaction: Omit<PendingReaction, "deadlineMs">): void {
  if (pending) {
    // Force-resolve any existing window before opening a new one.
    resolveReaction(pending.eventId, false);
  }

  const deadlineMs = Date.now() + REACTION_WINDOW_SEC * 1000;
  pending = { ...reaction, deadlineMs };

  EventBus.emit("combat:reaction_window", pending);

  cooldownClock.register(REACTION_TIMER_ID, REACTION_WINDOW_SEC, () => {
    if (pending?.eventId === reaction.eventId) {
      resolveReaction(reaction.eventId, false);
    }
  });
}

/** Called by UI when the player chooses to use or skip their reaction. */
export function resolveReaction(eventId: string, used: boolean): void {
  cooldownClock.unregister(REACTION_TIMER_ID);
  pending = null;
  EventBus.emit("combat:reaction_resolved", { eventId, used });
}

export function hasPendingReaction(): boolean {
  return pending !== null;
}
